'use client';

import { useEffect, useRef, useState } from 'react';
import '@/styles/responsive/tools.css';

const processPresets: Record<string, any> = {
  'customer-support': {
    hours: 20,
    hourlyCost: 18,
    errorCount: 8,
    errorCost: 75,
    automationLevel: 75,
    implementationCost: 2500,
  },
  'data-entry': {
    hours: 15,
    hourlyCost: 15,
    errorCount: 12,
    errorCost: 40,
    automationLevel: 90,
    implementationCost: 2000,
  },
  invoicing: {
    hours: 10,
    hourlyCost: 22,
    errorCount: 5,
    errorCost: 120,
    automationLevel: 85,
    implementationCost: 3000,
  },
  reporting: {
    hours: 12,
    hourlyCost: 30,
    errorCount: 10,
    errorCost: 200,
    automationLevel: 70,
    implementationCost: 3500,
  },
  'lead-followup': {
    hours: 8,
    hourlyCost: 25,
    errorCount: 15,
    errorCost: 150,
    automationLevel: 80,
    implementationCost: 2500,
  },
  scheduling: {
    hours: 6,
    hourlyCost: 20,
    errorCount: 5,
    errorCost: 50,
    automationLevel: 85,
    implementationCost: 1500,
  },
  'document-management': {
    hours: 10,
    hourlyCost: 20,
    errorCount: 7,
    errorCost: 60,
    automationLevel: 75,
    implementationCost: 2500,
  },
  'crm-updates': {
    hours: 5,
    hourlyCost: 22,
    errorCount: 10,
    errorCost: 80,
    automationLevel: 80,
    implementationCost: 1500,
  },
  'social-media': {
    hours: 8,
    hourlyCost: 18,
    errorCount: 5,
    errorCost: 40,
    automationLevel: 70,
    implementationCost: 1500,
  },
  'email-marketing': {
    hours: 6,
    hourlyCost: 25,
    errorCount: 8,
    errorCost: 100,
    automationLevel: 75,
    implementationCost: 2000,
  },
  other: {
    hours: 10,
    hourlyCost: 25,
    errorCount: 5,
    errorCost: 50,
    automationLevel: 80,
    implementationCost: 3000,
  },
};

export default function Calculator({ theme = 'dark' }: { theme?: 'dark' | 'light' }) {
  const [processType, setProcessType] = useState('customer-support');
  const [hoursPerWeek, setHoursPerWeek] = useState(20);
  const [hourlyCost, setHourlyCost] = useState(18);
  const [errorCount, setErrorCount] = useState(8);
  const [errorCost, setErrorCost] = useState(75);
  const [automationLevel, setAutomationLevel] = useState(75);
  const [implementationCost, setImplementationCost] = useState(2500);
  const [activePeriod, setActivePeriod] = useState('annual');
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [showCalculation, setShowCalculation] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowCalculation(false);
      }
    };

    if (showCalculation) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalculation]);

  useEffect(() => {
    const preset = processPresets[processType] || processPresets.other;
    setHoursPerWeek(preset.hours);
    setHourlyCost(preset.hourlyCost);
    setErrorCount(preset.errorCount);
    setErrorCost(preset.errorCost);
    setAutomationLevel(preset.automationLevel);
    setImplementationCost(preset.implementationCost);
  }, [processType]);

  const calculateSavings = () => {
    const timeSavings = hoursPerWeek * hourlyCost * 52 * (automationLevel / 100);
    const errorSavings = errorCount * errorCost * 52 * (automationLevel / 100);
    const totalSavings = timeSavings + errorSavings;

    const paybackPeriodMonths =
      implementationCost > 0 ? Math.ceil((implementationCost / totalSavings) * 12) : 0;
    const firstYearROI =
      implementationCost > 0
        ? Math.round(((totalSavings - implementationCost) / implementationCost) * 100)
        : 0;
    const threeYearSavings = totalSavings * 3;
    const threeYearROI =
      implementationCost > 0
        ? Math.round(((threeYearSavings - implementationCost) / implementationCost) * 100)
        : 0;

    return { totalSavings, paybackPeriodMonths, firstYearROI, threeYearROI };
  };

  const { totalSavings, paybackPeriodMonths, firstYearROI, threeYearROI } = calculateSavings();

  const getDisplayValues = () => {
    let displayValue = 0;
    let periodTitle = '';
    let periodSubtitle = '';

    switch (activePeriod) {
      case 'weekly':
        displayValue = Math.round(totalSavings / 52);
        periodTitle = 'Weekly Cost Savings';
        periodSubtitle = 'per week';
        break;
      case 'monthly':
        displayValue = Math.round(totalSavings / 12);
        periodTitle = 'Monthly Cost Savings';
        periodSubtitle = 'per month';
        break;
      default:
        displayValue = Math.round(totalSavings);
        periodTitle = 'Yearly Cost Savings';
        periodSubtitle = 'first year total';
        break;
    }

    const formattedValue = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(displayValue);

    return { formattedValue, periodTitle, periodSubtitle };
  };

  const { formattedValue, periodTitle, periodSubtitle } = getDisplayValues();

  const textColor = theme === 'dark' ? 'white' : 'black';

  return (
    <section id="calculator" className="calculator-section">
      <div className="calculator-container">
        <div className="calculator-header">
          <h2 className="calculator-title" style={{ color: textColor }}>
            Calculate Your Savings
          </h2>
          <p className="section-subtitle" style={{ color: textColor }}>
            Discover your exact savings potential with intelligent automation
          </p>
        </div>

        <div
          className="calculator-content"
          style={{ background: 'white', padding: '20px', borderRadius: '8px' }}
        >
          <div
            className="calculator-inputs"
            style={{ background: 'white' }}
            suppressHydrationWarning
          >
            <div className="input-group">
              <div className="compact-select-row">
                <div className="select-label" style={{ color: 'black' }}>
                  Process to Automate:
                </div>
                <select
                  id="process"
                  value={processType}
                  onChange={(e) => setProcessType(e.target.value)}
                  style={{ background: 'white', color: 'black' }}
                  aria-label="Process to Automate"
                >
                  <option value="customer-support">Customer Support</option>
                  <option value="data-entry">Data Entry</option>
                  <option value="invoicing">Invoicing</option>
                  <option value="reporting">Reporting</option>
                  <option value="lead-followup">Lead Follow-up</option>
                  <option value="scheduling">Scheduling</option>
                  <option value="document-management">Document Management</option>
                  <option value="crm-updates">CRM Updates</option>
                  <option value="social-media">Social Media Management</option>
                  <option value="email-marketing">Email Marketing</option>
                  <option value="other">Other</option>
                </select>

                <div className="advanced-options-toggle">
                  <a
                    href="#"
                    id="advanced-toggle-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAdvanced(!showAdvanced);
                    }}
                    style={{ color: 'black' }}
                    aria-label="Toggle Advanced Options"
                  >
                    <i
                      id="settings-icon"
                      className={`fas ${showAdvanced ? 'fa-times' : 'fa-cog'}`}
                      style={{ color: 'black' }}
                    />
                  </a>
                </div>

                <div className="label-with-tooltip tooltip-right">
                  <div className="tooltip-container">
                    <span className="tooltip-icon">i</span>
                    <span className="tooltip-text">Select the process you want to automate</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="advanced-options"
              className={showAdvanced ? 'expanded-content' : 'collapsed-content'}
              style={{ display: showAdvanced ? 'block' : 'none' }}
            >
              <div className="input-group">
                <div className="compact-slider-row">
                  <div className="slider-label">Hours Spent Weekly:</div>
                  <input
                    type="range"
                    id="hours-per-week"
                    className="slider"
                    min="1"
                    max="100"
                    value={hoursPerWeek}
                    onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                    aria-label="Hours Spent Weekly (Slider)"
                  />
                  <div className="input-with-symbol">
                    <input
                      type="number"
                      className="slider-input"
                      value={hoursPerWeek}
                      onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                      min="1"
                      max="100"
                      aria-label="Hours Spent Weekly"
                    />
                    <span className="input-symbol symbol-right">hrs</span>
                  </div>
                  <div className="label-with-tooltip tooltip-right">
                    <div className="tooltip-container">
                      <span className="tooltip-icon">i</span>
                      <span className="tooltip-text">
                        Include all staff time involved in this process
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="compact-slider-row">
                  <div className="slider-label">Hourly Staff Cost:</div>
                  <input
                    type="range"
                    id="hourly-cost"
                    className="slider"
                    min="10"
                    max="200"
                    value={hourlyCost}
                    onChange={(e) => setHourlyCost(Number(e.target.value))}
                    aria-label="hourly staff cost (slider)"
                  />
                  <div className="input-with-symbol">
                    <span className="input-symbol symbol-left">£</span>
                    <input
                      type="number"
                      className="slider-input"
                      value={hourlyCost}
                      onChange={(e) => setHourlyCost(Number(e.target.value))}
                      min="10"
                      max="200"
                      aria-label="hourly staff cost"
                    />
                  </div>
                  <div className="label-with-tooltip tooltip-right">
                    <div className="tooltip-container">
                      <span className="tooltip-icon">i</span>
                      <span className="tooltip-text">
                        Include salary + benefits + overhead costs
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="compact-slider-row">
                  <div className="slider-label">Avg. Errors Per Week:</div>
                  <input
                    type="range"
                    id="error-count"
                    className="slider"
                    min="0"
                    max="50"
                    value={errorCount}
                    onChange={(e) => setErrorCount(Number(e.target.value))}
                    aria-label="Avg. Errors Per Week (Slider)"
                  />
                  <div className="input-with-symbol">
                    <input
                      type="number"
                      className="slider-input"
                      value={errorCount}
                      onChange={(e) => setErrorCount(Number(e.target.value))}
                      min="0"
                      max="50"
                      aria-label="Avg. Errors Per Week"
                    />
                    <span className="input-symbol symbol-right">/wk</span>
                  </div>
                  <div className="label-with-tooltip tooltip-right">
                    <div className="tooltip-container">
                      <span className="tooltip-icon">i</span>
                      <span className="tooltip-text">Number of manual errors per week</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="compact-slider-row">
                  <div className="slider-label">Cost of Each Error:</div>
                  <input
                    type="range"
                    id="error-cost"
                    className="slider"
                    min="0"
                    max="500"
                    value={errorCost}
                    onChange={(e) => setErrorCost(Number(e.target.value))}
                    aria-label="Cost of Each Error (Slider)"
                  />
                  <div className="input-with-symbol">
                    <span className="input-symbol symbol-left">£</span>
                    <input
                      type="number"
                      className="slider-input"
                      value={errorCost}
                      onChange={(e) => setErrorCost(Number(e.target.value))}
                      min="0"
                      max="500"
                      aria-label="Cost of Each Error"
                    />
                  </div>
                  <div className="label-with-tooltip tooltip-right">
                    <div className="tooltip-container">
                      <span className="tooltip-icon">i</span>
                      <span className="tooltip-text">
                        Time to fix + customer impact + lost opportunities
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="compact-slider-row">
                  <div className="slider-label">Est. Automation Efficiency:</div>
                  <input
                    type="range"
                    id="automation-level"
                    className="slider"
                    min="10"
                    max="100"
                    value={automationLevel}
                    onChange={(e) => setAutomationLevel(Number(e.target.value))}
                    aria-label="Est. Automation Efficiency (Slider)"
                  />
                  <div className="input-with-symbol">
                    <input
                      type="number"
                      className="slider-input"
                      value={automationLevel}
                      onChange={(e) => setAutomationLevel(Number(e.target.value))}
                      min="10"
                      max="100"
                      aria-label="Est. Automation Efficiency"
                    />
                    <span className="input-symbol symbol-right">%</span>
                  </div>
                  <div className="label-with-tooltip tooltip-right">
                    <div className="tooltip-container">
                      <span className="tooltip-icon">i</span>
                      <span className="tooltip-text">
                        How much of this process can be automated
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-group">
                <div className="compact-slider-row">
                  <div className="slider-label">Upfront Investment:</div>
                  <input
                    type="range"
                    id="implementation-cost"
                    className="slider"
                    min="0"
                    max="10000"
                    step="100"
                    value={implementationCost}
                    onChange={(e) => setImplementationCost(Number(e.target.value))}
                    aria-label="Upfront Investment (Slider)"
                  />
                  <div className="input-with-symbol">
                    <span className="input-symbol symbol-left">£</span>
                    <input
                      type="number"
                      className="slider-input"
                      value={implementationCost}
                      onChange={(e) => setImplementationCost(Number(e.target.value))}
                      min="0"
                      max="10000"
                      step="100"
                      aria-label="Upfront Investment"
                    />
                  </div>
                  <div className="label-with-tooltip tooltip-right">
                    <div className="tooltip-container">
                      <span className="tooltip-icon">i</span>
                      <span className="tooltip-text">
                        One-time cost for setup and implementation
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="calculator-results" style={{ marginTop: 0 }}>
            <div className="primary-result">
              <div className="result-header">
                <div className="result-title">
                  <b id="savings-period-title">{periodTitle}</b>
                </div>
              </div>
              <div className="result-value" id="primary-savings-value">
                {formattedValue}
              </div>
              <div className="result-subtitle" id="savings-period-subtitle">
                {periodSubtitle}
              </div>

              <div className="period-toggle-container">
                <div className="period-toggle">
                  <button
                    onClick={() => setActivePeriod('annual')}
                    className={`toggle-btn ${activePeriod === 'annual' ? 'active' : ''}`}
                    data-period="annual"
                  >
                    Yearly
                  </button>
                  <button
                    onClick={() => setActivePeriod('monthly')}
                    className={`toggle-btn ${activePeriod === 'monthly' ? 'active' : ''}`}
                    data-period="monthly"
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setActivePeriod('weekly')}
                    className={`toggle-btn ${activePeriod === 'weekly' ? 'active' : ''}`}
                    data-period="weekly"
                  >
                    Weekly
                  </button>
                </div>
              </div>

              <div className="roi-grid">
                <div className="roi-item">
                  <div className="roi-label">Payback Period</div>
                  <div className="roi-value small" id="payback-period">
                    {paybackPeriodMonths > 0 ? `${paybackPeriodMonths} months` : 'Immediate'}
                  </div>
                </div>
                <div className="roi-item">
                  <div className="roi-label">First Year ROI</div>
                  <div className="roi-value" id="first-year-roi">
                    {firstYearROI.toLocaleString()}%
                  </div>
                </div>
                <div className="roi-item">
                  <div className="roi-label">3-Year ROI</div>
                  <div className="roi-value" id="three-year-roi">
                    {threeYearROI.toLocaleString()}%
                  </div>
                </div>
              </div>

              <div className="cta-section">
                <div className="cta-content">
                  <h3 id="dynamic-cta-text">Ready to save {formattedValue}?</h3>
                  <div className="cta-buttons">
                    <a href="#contact" className="btn-purple">
                      Book a Free Audit
                    </a>
                  </div>
                  <p className="cta-description">
                    An expert will confirm your exact savings potential in a free, 30-minute
                    consultation.
                  </p>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowCalculation(!showCalculation);
                    }}
                    className="cta-description"
                    style={{
                      cursor: 'pointer',
                      color: 'blue',
                      display: 'block',
                      marginTop: '10px',
                    }}
                  >
                    How it&apos;s calculated
                  </a>
                </div>
              </div>

              {showCalculation && (
                <div
                  id="calculation-content"
                  className="calculation-content"
                  style={{ display: 'block' }}
                  ref={popupRef}
                >
                  <button
                    className="calculation-close-btn"
                    onClick={() => setShowCalculation(false)}
                  >
                    ×
                  </button>
                  <p>Savings Calculation Formula:</p>
                  <div className="formula">
                    <div className="formula-part">
                      Time Savings = Hours × Wage × 52 weeks × Automation %
                    </div>
                    <div className="formula-part">
                      Error Savings = Errors Per Week × Error Cost × 52 weeks × Automation %
                    </div>
                    <div className="formula-part">
                      Total Savings = Time Savings + Error Savings - Implementation Cost
                    </div>
                  </div>
                  <p className="formula-note">*52 weeks represents a full year of savings</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
